import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { useApp } from '@/components/AppLayout';
import {
  reportsApi,
  type SalesTaxReport,
  type SalesTaxRow,
  type ReportExportFormat,
} from '@/lib/api';
import { formatAmount } from '@/lib/currency';
import { DataTable, SortableHeader } from '@/components/ui/data-table';
import {
  ReportLayout,
  SummaryCard,
  SummaryCardsGrid,
  ExportDropdown,
  TimeRangePicker,
  createTimeRange,
  type TimeRange,
} from '@/components/reports';
import type { ColumnDef } from '@tanstack/react-table';
import { Receipt } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Label } from '@/components/ui/label';

export default function SalesTaxSummary() {
  const { currentBusiness } = useApp();
  const [report, setReport] = useState<SalesTaxReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<TimeRange>(() => createTimeRange('this_year'));
  const [accountingMethod, setAccountingMethod] = useState<'cash' | 'accrual'>('accrual');

  useEffect(() => {
    if (currentBusiness) {
      loadReport();
    }
  }, [currentBusiness, timeRange, accountingMethod]);

  const loadReport = async () => {
    if (!currentBusiness) return;
    setLoading(true);
    try {
      const response = await reportsApi.getSalesTax(
        currentBusiness.id,
        timeRange.preset === 'custom' ? 'custom' : timeRange.preset,
        timeRange.preset === 'custom' ? format(timeRange.startDate, 'yyyy-MM-dd') : undefined,
        timeRange.preset === 'custom' ? format(timeRange.endDate, 'yyyy-MM-dd') : undefined,
        accountingMethod
      );
      if (response.data) {
        setReport(response.data);
        // If server returns a different method (from QB settings), use it on first load
        if (response.data.summary.accounting_method && response.data.summary.accounting_method !== accountingMethod) {
          setAccountingMethod(response.data.summary.accounting_method);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (exportFormat: ReportExportFormat) => {
    if (!currentBusiness) return;
    await reportsApi.exportSalesTax(
      currentBusiness.id,
      exportFormat,
      timeRange.preset === 'custom' ? 'custom' : timeRange.preset,
      timeRange.preset === 'custom' ? format(timeRange.startDate, 'yyyy-MM-dd') : undefined,
      timeRange.preset === 'custom' ? format(timeRange.endDate, 'yyyy-MM-dd') : undefined,
      false,
      accountingMethod
    );
  };

  const columns: ColumnDef<SalesTaxRow>[] = [
    {
      accessorKey: 'tax_name',
      header: ({ column }) => <SortableHeader column={column} title="Tax Name" />,
      cell: ({ row }) => (
        <div className="font-medium">{row.getValue('tax_name')}</div>
      ),
    },
    {
      accessorKey: 'tax_rate',
      header: ({ column }) => (
        <div className="flex justify-end">
          <SortableHeader column={column} title="Rate" />
        </div>
      ),
      cell: ({ row }) => (
        <div className="text-right">{row.getValue('tax_rate')}%</div>
      ),
    },
    {
      accessorKey: 'tax_type',
      header: 'Type',
      cell: ({ row }) => {
        const type = row.getValue('tax_type') as string;
        return (
          <Badge variant={type === 'exclusive' ? 'secondary' : 'outline'}>
            {type === 'exclusive' ? 'Exclusive' : 'Inclusive'}
          </Badge>
        );
      },
    },
    {
      accessorKey: 'invoice_count',
      header: ({ column }) => (
        <div className="flex justify-end">
          <SortableHeader column={column} title="Invoices" />
        </div>
      ),
      cell: ({ row }) => (
        <div className="text-right">{row.getValue('invoice_count')}</div>
      ),
    },
    {
      accessorKey: 'taxable_sales',
      header: ({ column }) => (
        <div className="flex justify-end">
          <SortableHeader column={column} title="Taxable Sales" />
        </div>
      ),
      cell: ({ row }) => (
        <div className="text-right">
          {formatAmount(row.getValue('taxable_sales'))}
        </div>
      ),
    },
    {
      accessorKey: 'tax_collected',
      header: ({ column }) => (
        <div className="flex justify-end">
          <SortableHeader column={column} title="Tax Collected" />
        </div>
      ),
      cell: ({ row }) => (
        <div className="text-right font-medium text-green-600">
          {formatAmount(row.getValue('tax_collected'))}
        </div>
      ),
    },
  ];

  return (
    <ReportLayout
      title="Sales Tax Summary"
      actions={<ExportDropdown onExport={handleExport} disabled={loading || !report} />}
      filters={
        <div className="flex flex-wrap items-center gap-4">
          <TimeRangePicker
            value={timeRange}
            onChange={setTimeRange}
          />
          <div className="flex items-center gap-2">
            <Label className="text-sm text-muted-foreground">Method:</Label>
            <ToggleGroup
              type="single"
              value={accountingMethod}
              onValueChange={(value) => value && setAccountingMethod(value as 'cash' | 'accrual')}
              className="border rounded-md"
            >
              <ToggleGroupItem value="accrual" aria-label="Accrual basis" className="text-xs px-3">
                Accrual
              </ToggleGroupItem>
              <ToggleGroupItem value="cash" aria-label="Cash basis" className="text-xs px-3">
                Cash
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
        </div>
      }
    >
      {report && (
        <>
          <div className="mb-6">
            <SummaryCardsGrid>
              <SummaryCard
                label="Total Taxable Sales"
                value={formatAmount(report.summary.total_taxable_sales)}
              />
              <SummaryCard
                label="Total Tax Collected"
                value={formatAmount(report.summary.total_tax_collected)}
                variant="success"
              />
              <SummaryCard
                label="Tax Types"
                value={report.summary.tax_count.toString()}
              />
            </SummaryCardsGrid>
          </div>

          <div className="mb-4 p-3 bg-muted/50 rounded-lg text-sm text-muted-foreground">
            {accountingMethod === 'cash' ? (
              <>
                <strong>Cash Basis:</strong> Tax is reported when invoices are fully paid.
                Only paid invoices are included, filtered by payment date.
              </>
            ) : (
              <>
                <strong>Accrual Basis:</strong> Tax is reported when invoices are issued.
                Includes both unpaid and paid invoices, filtered by invoice date.
              </>
            )}
          </div>
        </>
      )}
      <DataTable
        columns={columns}
        data={report?.data ?? []}
        loading={loading}
        emptyIcon={<Receipt className="h-6 w-6 text-muted-foreground" />}
        emptyTitle="No Tax Data"
        emptyDescription="No taxable sales found in this period. Make sure your items have taxes assigned."
      />
    </ReportLayout>
  );
}
